import { useState } from 'react';
import {
    Box,
    Button,
    Container,
    IconButton,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ErrorTryRefetch } from '../../core/ErrorTryRefetch';
import { useAlert } from '../../core/AlertContext';
import {
    useBlogComments,
    useDownloadBlogImagesZip,
    useBlogImages,
    useBlogPost,
    useCreateBlogComment,
    useDeleteBlogComment,
    useDeleteBlogImage,
    useDeleteBlogPost,
    useToggleBlogCommentReaction,
    useUploadBlogImages,
} from '../blogHooks';
import { BlogReactionKind } from '../blogSchemas';
import { canEditBlogPost } from '../blogPermissions';
import { formatDateTime, parseApiErrorMessage } from '../blogUtils';
import BlogImageGallery from '../components/BlogImageGallery';
import BlogImageGalleryDialog from '../components/BlogImageGalleryDialog';
import BlogCommentSection from '../components/BlogCommentSection';
import DetailScreenHeader from '../../core/DetailScreenHeader';
import PvtaCard from '../../core/PvtaCard';
import TagChip from '../../core/TagChip';
import { GREEN_900, cardLgSx, dangerButtonSx, secondaryButtonSx } from '../../core/pvtaStyles';
import { fetchAuthenticatedBlobByUrl } from '../../hooks/useAuthenticatedImage';

const BlogDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { id } = useParams<{ id: string }>();
    const postId = Number(id ?? 0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: post, isLoading, isError, refetch } = useBlogPost(postId);
    const {
        data: comments = [],
        isLoading: isLoadingComments,
    } = useBlogComments(postId);
    const {
        data: images = [],
    } = useBlogImages(postId);

    const createComment = useCreateBlogComment();
    const deleteComment = useDeleteBlogComment();
    const toggleReaction = useToggleBlogCommentReaction();
    const uploadImages = useUploadBlogImages();
    const deleteImage = useDeleteBlogImage();
    const downloadImagesZip = useDownloadBlogImagesZip();
    const deletePost = useDeleteBlogPost();
    const [galleryOpen, setGalleryOpen] = useState<boolean>(false);
    const [gallerySelectedIndex, setGallerySelectedIndex] = useState<number | null>(null);
    const currentUserId = Number(localStorage.getItem('user_id') ?? 0) || null;
    const isFullscreenMobile = isMobile;

    const canManagePost = post ? canEditBlogPost(post.author.id) : false;

    const handleDeletePost = async (): Promise<void> => {
        if (!post) {
            return;
        }
        try {
            await deletePost.mutateAsync({ id: post.id });
            showAlert({ severity: 'success', text: 'Beitrag gelöscht.' });
            navigate('/blog');
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Beitrag konnte nicht gelöscht werden.') });
        }
    };

    const handleCreateComment = async (text: string, parentId?: number | null): Promise<void> => {
        await createComment.mutateAsync({
            postId,
            input: { text, parent_id: parentId ?? undefined },
        });
        if (!parentId) {
            showAlert({ severity: 'success', text: 'Kommentar erstellt.' });
        }
    };

    const handleDeleteComment = async (commentId: number): Promise<void> => {
        await deleteComment.mutateAsync({ postId, commentId });
    };

    const handleToggleReaction = async (commentId: number, emoji: BlogReactionKind): Promise<void> => {
        await toggleReaction.mutateAsync({ postId, commentId, emoji });
    };

    const handleUploadImages = async (files: File[]): Promise<void> => {
        try {
            await uploadImages.mutateAsync({ postId, files });
            showAlert({ severity: 'success', text: `${files.length} Bild(er) hochgeladen.` });
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Bildupload fehlgeschlagen.') });
        }
    };

    const handleDeleteImage = async (imageId: number): Promise<void> => {
        try {
            await deleteImage.mutateAsync({ imageId, postId });
            showAlert({ severity: 'success', text: 'Bild gelöscht.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Bild konnte nicht gelöscht werden.') });
        }
    };

    const handleOpenGallery = (selectedIndex?: number): void => {
        setGallerySelectedIndex(selectedIndex ?? null);
        setGalleryOpen(true);
    };

    const handleDownloadImage = async (image: (typeof images)[number]): Promise<void> => {
        try {
            const blob = await fetchAuthenticatedBlobByUrl(image.url);
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = image.caption.trim() || image.originalFilename;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Bild konnte nicht heruntergeladen werden.') });
        }
    };

    const handleDownloadZip = async (imageIds: number[]): Promise<void> => {
        try {
            const zipBlob = await downloadImagesZip.mutateAsync({ postId, imageIds });
            const objectUrl = URL.createObjectURL(zipBlob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = `blog-post-${postId}-photos.zip`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Bilder konnten nicht als ZIP heruntergeladen werden.') });
        }
    };

    if (isError) {
        return <ErrorTryRefetch refetchFunction={() => void refetch()} />;
    }

    return (
        <Box
            sx={{
                py: { xs: 0, md: 5 },
                minHeight: { xs: '100dvh', md: 'auto' },
                backgroundColor: { xs: 'var(--pvta-surface)', md: 'transparent' },
            }}
        >
            <Container
                maxWidth="md"
                disableGutters={isFullscreenMobile}
                sx={{ px: { xs: 0, sm: 2, md: 3 }, minWidth: 0 }}
            >
                {isLoading && (
                    <PvtaCard
                        large
                        sx={{
                            borderRadius: { xs: 0, md: 'var(--pvta-radius-lg)' },
                            border: { xs: 'none', md: '1px solid var(--pvta-border)' },
                            boxShadow: { xs: 'none', md: 'var(--pvta-shadow-card)' },
                            minHeight: { xs: '100dvh', md: 'auto' },
                            p: { xs: 2.5, md: 4 },
                        }}
                    >
                        <Skeleton variant="text" width="70%" height={48} />
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="rectangular" height={260} sx={{ mt: 2, borderRadius: 'var(--pvta-radius-md)' }} />
                    </PvtaCard>
                )}

                {!isLoading && post && (
                    <Stack spacing={{ xs: 2.5, md: 3 }} sx={{ minWidth: 0 }}>
                        <Box
                            sx={[
                                cardLgSx,
                                {
                                    minWidth: 0,
                                    borderRadius: { xs: 0, md: 'var(--pvta-radius-lg)' },
                                    border: { xs: 'none', md: '1px solid var(--pvta-border)' },
                                    boxShadow: { xs: 'none', md: 'var(--pvta-shadow-card)' },
                                    p: { xs: 2.5, sm: 3, md: 4 },
                                },
                            ]}
                        >
                            {isMobile ? (
                                <Box sx={{ mx: -2.5, mt: -2.5, mb: 2.5 }}>
                                    <DetailScreenHeader
                                        title={post.title}
                                        onClose={() => navigate('/blog')}
                                        actions={canManagePost ? (
                                            <>
                                                <IconButton
                                                    aria-label="beitrag bearbeiten"
                                                    onClick={() => navigate(`/blog/${post.id}/edit`)}
                                                    sx={{
                                                        minHeight: 44,
                                                        minWidth: 44,
                                                        border: '1px solid',
                                                        borderColor: 'var(--pvta-border)',
                                                    }}
                                                >
                                                    <EditOutlinedIcon />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="beitrag löschen"
                                                    onClick={() => void handleDeletePost()}
                                                    sx={{
                                                        minHeight: 44,
                                                        minWidth: 44,
                                                        border: '1px solid',
                                                        borderColor: 'var(--pvta-border)',
                                                    }}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </>
                                        ) : undefined}
                                    />
                                </Box>
                            ) : (
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                                    spacing={2}
                                    sx={{ mb: 2 }}
                                >
                                    <Typography
                                        component="h1"
                                        sx={{
                                            fontFamily: 'Georgia, "Times New Roman", serif',
                                            fontWeight: 700,
                                            fontSize: { xs: 28, md: 36 },
                                            lineHeight: 1.15,
                                            color: GREEN_900,
                                            minWidth: 0,
                                        }}
                                    >
                                        {post.title}
                                    </Typography>
                                    {canManagePost && (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            <Button
                                                size="small"
                                                startIcon={<EditOutlinedIcon />}
                                                onClick={() => navigate(`/blog/${post.id}/edit`)}
                                                sx={secondaryButtonSx}
                                            >
                                                Bearbeiten
                                            </Button>
                                            <Button
                                                size="small"
                                                startIcon={<DeleteOutlineIcon />}
                                                onClick={() => void handleDeletePost()}
                                                sx={dangerButtonSx}
                                            >
                                                Löschen
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--pvta-muted)',
                                    mb: { xs: 2.5, md: 3 },
                                    pb: { xs: 2.5, md: 3 },
                                    borderBottom: '1px solid var(--pvta-border)',
                                }}
                            >
                                {post.author.name} • erstellt am {formatDateTime(post.createdAt)}
                                {post.updatedAt ? ` • geändert am ${formatDateTime(post.updatedAt)}` : ''}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.8,
                                    fontSize: { xs: 16, md: 17 },
                                    color: 'var(--pvta-text)',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {post.content}
                            </Typography>
                            {post.tags.length > 0 && (
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: { xs: 2.5, md: 3 } }}>
                                    {post.tags.map((tag) => (
                                        <TagChip key={tag.id} tag={tag} />
                                    ))}
                                </Stack>
                            )}
                        </Box>

                        <BlogImageGallery
                            images={images}
                            canManage={canManagePost}
                            isUploading={uploadImages.isPending}
                            onUpload={handleUploadImages}
                            onDelete={handleDeleteImage}
                            onOpenGallery={() => handleOpenGallery()}
                            onOpenImage={(index) => handleOpenGallery(index)}
                        />

                        <BlogCommentSection
                            comments={comments}
                            isLoading={isLoadingComments}
                            isSubmitting={createComment.isPending || deleteComment.isPending || toggleReaction.isPending}
                            onCreate={handleCreateComment}
                            onDelete={handleDeleteComment}
                            onToggleReaction={handleToggleReaction}
                        />
                    </Stack>
                )}
            </Container>
            <BlogImageGalleryDialog
                open={galleryOpen}
                images={images}
                currentUserId={currentUserId}
                initialSelectedIndex={gallerySelectedIndex}
                isDeleting={deleteImage.isPending}
                isUploading={uploadImages.isPending}
                isDownloadingZip={downloadImagesZip.isPending}
                onClose={() => {
                    setGalleryOpen(false);
                    setGallerySelectedIndex(null);
                }}
                onDelete={handleDeleteImage}
                onDownload={handleDownloadImage}
                onDownloadZip={handleDownloadZip}
                onUpload={handleUploadImages}
            />
        </Box>
    );
};

export default BlogDetailPage;
